import { useEffect, useState, type RefObject } from "react"

import { useSupportsIntersectionObserver } from "./useSupportsIntersectionObserver"

/**
 * Whether an element is on screen, or within 160px of it, so animations can
 * start just before they scroll into view and stop once they leave. Reports
 * visible when IntersectionObserver isn't available, so nothing stays frozen.
 */
export function useIsVisible(targetRef: RefObject<HTMLElement | null>): boolean {
  const supportsIntersectionObserver = useSupportsIntersectionObserver()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const target = targetRef.current
    if (!target || !supportsIntersectionObserver) return

    const observer = new IntersectionObserver(
      (entries) => setIsVisible(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: "160px 0px", threshold: 0 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [supportsIntersectionObserver, targetRef])

  return isVisible || !supportsIntersectionObserver
}
